import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../styles/EditUserModal.css";
import { getMarketSymbols } from "../services/api";
import TradingPairsSelector, { normalizeTradingPairs } from "./TradingPairsSelector";

const ApprovePreregModal = ({ open, item, onClose, onApprove, loading, error, success }) => {
  const [fullName, setFullName] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  const [tradingPairs, setTradingPairs] = useState([]);
  const [tradeMode, setTradeMode] = useState("");
  const [dashboardView, setDashboardView] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [telegramChatIds, setTelegramChatIds] = useState("");
  const [symbolOptions, setSymbolOptions] = useState([]);
  const [symbolsLoading, setSymbolsLoading] = useState(false);
  const [symbolsError, setSymbolsError] = useState("");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (open) {
      setFullName(item?.full_name || "");
      setMobilePhone(item?.mobile_phone || "");
      setTradingPairs(normalizeTradingPairs(item?.trading_pairs || [], item?.symbol || ""));
      setTradeMode((item?.trade_mode || "").toUpperCase());
      setDashboardView((item?.dashboard_view || "").toUpperCase());
      setApiKey("");
      setApiSecret("");
      setTelegramChatIds(Array.isArray(item?.telegram_chat_ids) ? item.telegram_chat_ids.join(", ") : item?.telegram_chat_ids || "");
      setFormError("");
    }
  }, [open, item]);

  const hasSymbolOptions = useMemo(() => {
    if (Array.isArray(symbolOptions)) return symbolOptions.length > 0;
    return Boolean(symbolOptions?.spot_symbols?.length || symbolOptions?.futures_symbols?.length || symbolOptions?.symbols?.length);
  }, [symbolOptions]);

  const loadSymbols = useCallback(async (force = false) => {
    if (symbolsLoading) return;
    if (!force && hasSymbolOptions) return;
    setSymbolsLoading(true);
    setSymbolsError("");
    const result = await getMarketSymbols();
    if (!result.success) {
      setSymbolsError(result.error || "No se pudo cargar los pares");
      setSymbolOptions([]);
    } else {
      setSymbolOptions(result.data || {});
    }
    setSymbolsLoading(false);
  }, [hasSymbolOptions, symbolsLoading]);

  useEffect(() => {
    if (open) {
      void loadSymbols(false);
    }
  }, [open, loadSymbols]);

  const normalizedPairs = useMemo(() => normalizeTradingPairs(tradingPairs), [tradingPairs]);

  if (!open || !item) return null;
  const showTradeFields = true;

  const handleSubmit = (event) => {
    event.preventDefault();
    const normalizedView = String(dashboardView || "").trim().toUpperCase();
    const normalizedMode = String(tradeMode || "").trim().toUpperCase();
    const normalizedSymbol = normalizedPairs[0]?.symbol || "";
    if (!normalizedView) {
      setFormError("Selecciona la vista del usuario.");
      return;
    }
    if (showTradeFields && !normalizedMode) {
      setFormError("Selecciona si la cuenta estará en simulación o producción.");
      return;
    }
    if (showTradeFields && !normalizedPairs.length) {
      setFormError("Agrega al menos un par de operación.");
      return;
    }
    setFormError("");
    onApprove?.({
      email: item.username,
      full_name: fullName,
      mobile_phone: mobilePhone,
      symbol: normalizedSymbol,
      trading_pairs: normalizedPairs,
      trade_mode: normalizedMode,
      dashboard_view: normalizedView,
      pionex_api_key: apiKey,
      pionex_api_secret: apiSecret,
      telegram_chat_ids: telegramChatIds,
    });
  };

  return (
    <div className="edit-user-backdrop" onClick={() => onClose?.()}>
      <div className="edit-user-modal" onClick={(event) => event.stopPropagation()}>
        <header className="edit-user-header">
          <div>
            <h3>Aprobar preregistro</h3>
            <p>{item?.username}</p>
          </div>
          <button type="button" className="edit-user-close" onClick={() => onClose?.()} aria-label="Cerrar">✕</button>
        </header>
        {formError ? <div className="edit-user-alert error">{formError}</div> : null}
        {error ? <div className="edit-user-alert error">{error}</div> : null}
        {success ? <div className="edit-user-alert success">{success}</div> : null}
        <form className="edit-user-form" onSubmit={handleSubmit}>
          <label className="edit-user-field"><span>Nombre</span><input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} disabled={loading} /></label>
          <label className="edit-user-field"><span>Móvil</span><input type="text" value={mobilePhone} onChange={(e) => setMobilePhone(e.target.value)} disabled={loading} /></label>
          <label className="edit-user-field"><span>Vista</span><select value={dashboardView} onChange={(e) => setDashboardView(e.target.value)} disabled={loading}><option value="">Selecciona una vista</option><option value="BOTH">Ambas</option><option value="TRADE">Trade</option></select></label>
          {showTradeFields ? (
            <>
              <label className="edit-user-field">
                <span>Pares de operación</span>
                <TradingPairsSelector
                  value={tradingPairs}
                  onChange={setTradingPairs}
                  symbolOptions={symbolOptions}
                  symbolsLoading={symbolsLoading}
                  symbolsError={symbolsError}
                  onRefreshSymbols={() => loadSymbols(true)}
                  disabled={loading}
                />
              </label>
              <label className="edit-user-field"><span>Modo</span><select value={tradeMode} onChange={(e) => setTradeMode(e.target.value)} disabled={loading}><option value="">Selecciona modo</option><option value="SIM">Simulación</option><option value="REAL">Producción</option></select></label>
              <label className="edit-user-field"><span>Pionex API Key</span><input type="text" placeholder="Vacío = usar la del preregistro" value={apiKey} onChange={(e) => setApiKey(e.target.value)} disabled={loading} /></label>
              <label className="edit-user-field"><span>Pionex API Secret</span><input type="text" placeholder="Vacío = usar la del preregistro" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)} disabled={loading} /></label>
              <label className="edit-user-field"><span>Telegram Chat IDs</span><input type="text" value={telegramChatIds} onChange={(e) => setTelegramChatIds(e.target.value)} disabled={loading} /></label>
            </>
          ) : null}
          <div className="edit-user-actions">
            <button type="button" className="edit-user-secondary" onClick={() => onClose?.()} disabled={loading}>Cancelar</button>
            <button type="submit" className="edit-user-primary" disabled={loading}>{loading ? "Aprobando..." : "Aprobar"}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApprovePreregModal;
